/**
 * WebSocketProvider - singleton WebSocket connection manager.
 *
 * Provides a single underlying WebSocket connection shared across all
 * consumers in the component tree. Handles:
 *   - Connection lifecycle (open, close, reconnect) keyed to auth state
 *   - Exponential backoff reconnect with +/-10% jitter (max 3 attempts)
 *   - Subscribe/unsubscribe topic multiplexing
 *   - Ping keepalive (every 30s while connected)
 *   - Dev-mode global test injection hook (gated behind isDev)
 *
 * Usage:
 *   <WebSocketProvider>
 *     <ComponentThatCallsUseWebSocket />
 *   </WebSocketProvider>
 *
 * Architecture note:
 *   WebSocketProvider sits BELOW AuthProvider in main.tsx so it can read
 *   the authenticated user/token without breaking the auth context hierarchy.
 *
 * Plan 03-06 implementation of the API-09 WebSocket client.
 */
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@/features/auth/useAuth';
import { authStorage } from '@/lib/auth-storage';
import { tryRefreshAccessToken } from '@/lib/api-client';
import { env, isDev } from '@/lib/env';
import { computeBackoff } from './reconnect';
import { parseServerFrame, buildSubscribeMsg, buildUnsubscribeMsg, buildPingMsg } from './schemas';
import type { ServerFrame, SubscriptionHandle } from './schemas';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Connection lifecycle status. */
export type WebSocketStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

/** Message handler callback. */
export type FrameHandler = (frame: ServerFrame) => void;

/** The context value exposed by WebSocketProvider. */
export interface WebSocketContextValue {
  /** Current connection status. */
  status: WebSocketStatus;
  /**
   * Subscribe to a topic. Sends {action:'subscribe', topic} on the socket.
   * Returns a handle indicating success. Safe to call before connected
   * (queued and sent on connect).
   */
  subscribe: (topic: string) => SubscriptionHandle;
  /**
   * Unsubscribe from a topic. Sends {action:'unsubscribe', topic}.
   * No-op if not currently subscribed.
   */
  unsubscribe: (topic: string) => void;
  /**
   * Register a frame handler. Called for every incoming server frame.
   * Returns an unregister function (call in useEffect cleanup).
   */
  onMessage: (handler: FrameHandler) => () => void;
  /** Resets backoff and attempts a fresh connection. Call after manual user action. */
  retry: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/** Internal context - do not consume outside useWebSocket(). */
export const WebSocketContext = createContext<WebSocketContextValue | null>(null);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum consecutive reconnect attempts before giving up. */
const MAX_ATTEMPTS = 3;

/** Ping interval in milliseconds while connected. */
const PING_INTERVAL_MS = 30_000;

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface Props {
  children: ReactNode;
}

/**
 * WebSocketProvider component.
 *
 * Mount once at the app root, below AuthProvider.
 * All useWebSocket() consumers within the tree share the single connection.
 *
 * @param children - Component tree with access to the WebSocket context
 */
export function WebSocketProvider({ children }: Props) {
  const { status: authStatus } = useAuth();

  const [status, setStatus] = useState<WebSocketStatus>('idle');
  const socketRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef<Set<FrameHandler>>(new Set());
  const attemptRef = useRef<number>(0);
  const subscriptionsRef = useRef<Set<string>>(new Set());
  // Guard against StrictMode double-effect invocation
  const connectingRef = useRef<boolean>(false);

  // ---------------------------------------------------------------------------
  // connect
  // ---------------------------------------------------------------------------

  const connect = useCallback(() => {
    const token = authStorage.getAccess();
    if (!token) return;

    // Prevent duplicate connections from StrictMode double-invoke
    if (connectingRef.current) return;
    connectingRef.current = true;

    setStatus(attemptRef.current === 0 ? 'connecting' : 'reconnecting');

    const url = `${env.FLEET_WS_URL}/v1/ws?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    socketRef.current = ws;

    ws.onopen = () => {
      attemptRef.current = 0;
      connectingRef.current = false;
      // Re-subscribe all active topics after reconnect
      for (const topic of subscriptionsRef.current) {
        ws.send(buildSubscribeMsg(topic));
      }
    };

    ws.onmessage = (ev: MessageEvent<string>) => {
      const frame = parseServerFrame(ev.data);
      if (frame.type === 'welcome') {
        setStatus('connected');
      }
      // Dispatch to all registered handlers (pong is still dispatched for completeness)
      handlersRef.current.forEach((h) => h(frame));
    };

    ws.onclose = (closeEv: CloseEvent) => {
      void closeEv; // available for debugging; code is not used in reconnect decision
      socketRef.current = null;
      connectingRef.current = false;

      // Clean disconnect (logout) - do not reconnect
      if (authStatus !== 'authenticated') {
        setStatus('disconnected');
        return;
      }

      if (attemptRef.current >= MAX_ATTEMPTS) {
        setStatus('disconnected');
        window.dispatchEvent(new CustomEvent('signalsentry:ws:paused'));
        return;
      }

      const delay = computeBackoff(attemptRef.current);
      attemptRef.current++;
      setStatus('reconnecting');
      // The socket most often closes because the 15-minute access token baked
      // into the ?token= query param expired (server closes 1008). Reusing the
      // stale token would just 1008-close again, burning the 3 attempts and
      // pausing the live feed. Refresh the token (shared, de-duped with
      // apiFetch + the live-data poller) BEFORE reconnecting so connect() reads
      // a fresh token from storage. `.finally(connect)` still reconnects on a
      // non-auth close (network blip) where refresh returns null / is
      // unnecessary; the authStatus guard above already handled a truly ended
      // session.
      setTimeout(() => {
        void tryRefreshAccessToken().finally(() => connect());
      }, delay);
    };

    ws.onerror = () => {
      // Error events are always followed by close; handle in ws.onclose
    };

    // Dev-mode test injection hook
    // Allows Playwright or console scripts to inject test frames:
    //   window.__signalsentry_ws_test_inject?.({ type: 'alert', payload: {...} })
    if (isDev) {
      (window as Window & { __signalsentry_ws_test_inject?: (frame: ServerFrame) => void }).__signalsentry_ws_test_inject = (frame: ServerFrame) => {
        const parsed = parseServerFrame(JSON.stringify(frame));
        if (parsed.type === 'welcome') setStatus('connected');
        handlersRef.current.forEach((h) => h(parsed));
      };
    }
  }, [authStatus]);

  // ---------------------------------------------------------------------------
  // Auth-driven lifecycle
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (authStatus === 'authenticated' && status === 'idle') {
      connect();
    }
    if (authStatus === 'unauthenticated' && socketRef.current) {
      socketRef.current.close(1000, 'logout');
      setStatus('disconnected');
    }
  }, [authStatus, status, connect]);

  // ---------------------------------------------------------------------------
  // Ping loop
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (status !== 'connected') return;
    const interval = setInterval(() => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(buildPingMsg());
      }
    }, PING_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [status]);

  // ---------------------------------------------------------------------------
  // API
  // ---------------------------------------------------------------------------

  const subscribe = useCallback((topic: string): SubscriptionHandle => {
    subscriptionsRef.current.add(topic);
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(buildSubscribeMsg(topic));
    }
    return { topic, ok: true };
  }, []);

  const unsubscribe = useCallback((topic: string): void => {
    subscriptionsRef.current.delete(topic);
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(buildUnsubscribeMsg(topic));
    }
  }, []);

  const onMessage = useCallback((handler: FrameHandler): (() => void) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const retry = useCallback((): void => {
    attemptRef.current = 0;
    connectingRef.current = false;
    setStatus('idle');
    connect();
  }, [connect]);

  const value: WebSocketContextValue = {
    status,
    subscribe,
    unsubscribe,
    onMessage,
    retry,
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}
